import 'react-datepicker/dist/react-datepicker.css';
import './date-picker-wrapper.scss';
import React, { useEffect, useMemo, useState } from 'react';
import CustomButton from '../custom-button/custom-button';
import ReactDatePicker from 'react-datepicker';
import {
    addDaysByDate,
    insightsHourFormat,
    insightsShortDateFormat,
    isValidHour,
    subtractDaysByDate, subtractDaysFromToday
} from '../../../utils/datesUtils';
import { Input } from 'antd';
import moment from 'moment';

interface DatePickerProps {
    initialEndDate: Date;
    initialStartDate: Date;
    maxDate: Date;
    allowedDaysAgo?: number;
    onClickOutside?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    cancelDatePicker: () => void;
    applyChanges: (dates:[Date, Date]) => void;
    enableHoursEditing?: boolean;
    automaticSelectRangeNum?: number;
}

export const DatePickerWrapper: React.FC<DatePickerProps> = ({ onClickOutside, cancelDatePicker, applyChanges,
                                                                 initialEndDate, initialStartDate,
                                                                 maxDate, allowedDaysAgo, enableHoursEditing, automaticSelectRangeNum }) => {
    const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate);
    const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate);
    const [selectedStartHour, setSelectedStartHour] = useState(insightsHourFormat(initialStartDate));
    const [selectedEndHour, setSelectedEndHour] = useState(insightsHourFormat(initialEndDate));

    useEffect(() => {
        setSelectedStartHour(insightsHourFormat(selectedStartDate));
    }, [selectedStartDate]);

    useEffect(() => {
        setSelectedEndHour(insightsHourFormat(selectedEndDate));
    }, [selectedEndDate]);

    const maxAllowedDate = useMemo(() => {
        if (selectedStartDate && !selectedEndDate) {
            return moment.min(moment(maxDate), moment(addDaysByDate(89, selectedStartDate))).toDate();
        }
        return maxDate;
    }, [selectedStartDate, selectedEndDate,  maxDate]);

    const innerApplyChanges  = () => {
        const [sHours, sMins] = selectedStartHour.split(':');
        selectedStartDate.setHours(Number(sHours), Number(sMins));
        const [eHours, eMins] = selectedEndHour.split(':');
        selectedEndDate.setHours(Number(eHours), Number(eMins));
        applyChanges([selectedStartDate, selectedEndDate]);
    };

    const isApplyDisabled = useMemo(() => {
        return !isValidHour(selectedStartHour) || !isValidHour(selectedEndHour) ||!selectedStartDate || !selectedEndDate;
    }, [selectedStartHour, selectedEndHour, selectedStartDate, selectedEndDate]);

    const innerOnChange = (date: [Date, Date]) => {
        const [startDate, endDate] = date;
        if (automaticSelectRangeNum !== undefined) {
            setSelectedEndDate(startDate);
            setSelectedStartDate(subtractDaysByDate(automaticSelectRangeNum, startDate));
        } else {
            startDate?.setHours(0,0);
            setSelectedStartDate(startDate);
            endDate?.setHours(23, 59);
            setSelectedEndDate(endDate);
        }
    };

    const createApplyAndCancelButtons = useMemo(() => <div className="buttons-apply-date-picker">
        {enableHoursEditing && selectedStartDate && <div className={'hours start flex'}>
            <div className={'from-to'}><span className="bold-words">From</span> {insightsShortDateFormat(selectedStartDate)}</div>
            <Input value={selectedStartHour} onChange={(e) => setSelectedStartHour(e.target.value)} className={'from-hour'}
                   status={isValidHour(selectedStartHour)? '' : 'error'}/>
        </div>}
        {enableHoursEditing && selectedEndDate && <div className={'hours end flex'}>
            <div className={'from-to'}><span className="bold-words">To</span> {insightsShortDateFormat(selectedEndDate)}</div>
            <Input value={selectedEndHour} onChange={(e) => setSelectedEndHour(e.target.value)} className={'to-hour'}
                   status={isValidHour(selectedEndHour)? '' : 'error'}/>
        </div>}
        <div className={'action flex'}>
            <button type="button" className="cancel-button" onClick={cancelDatePicker}>Cancel</button>
            <CustomButton className="primary-btn apply" onClick={innerApplyChanges} label="Apply" isDisabled={isApplyDisabled}/>
        </div>
    </div>, [selectedStartDate, selectedEndDate, selectedStartHour, selectedEndHour]);

    return <ReactDatePicker calendarClassName="date-picker-component"
                            onChange={innerOnChange}
                            selected={selectedEndDate}
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            minDate={allowedDaysAgo ? subtractDaysFromToday(allowedDaysAgo) : undefined}
                            maxDate={maxAllowedDate}
                            onClickOutside={onClickOutside}
                            monthsShown={2}
                            selectsRange
        // showPreviousMonths // -need check this  property.its cause to jump a month for any click on date
                            inline>
        {createApplyAndCancelButtons}
    </ReactDatePicker>;
};
